// Admin: BullMQ queue-monitoring. PRD-00 §18 + PRD-11 §3.9.

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";

interface QueueStat {
  name: string;
  cron: string | null;
  concurrency: number;
  deadLetter: string | null;
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
  };
}

export function AdminQueuesPage() {
  const { t } = useTranslation();

  const q = useQuery({
    queryKey: ["queues"],
    queryFn: () => api.get<{ queues: QueueStat[] }>("/v1/admin/queues"),
    refetchInterval: 5_000,
  });

  return (
    <AdminLayout title={t("admin.queues.title", "Queues")}>
      {q.isLoading ? (
        <p className="text-sm text-neutral-500">{t("loading", "Laden…")}</p>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-5 py-2 font-medium">{t("admin.queues.col.name", "Queue")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("admin.queues.col.cron", "Cron")}</th>
                <th className="text-right px-3 py-2 font-medium">{t("admin.queues.col.waiting", "Wachtend")}</th>
                <th className="text-right px-3 py-2 font-medium">{t("admin.queues.col.active", "Actief")}</th>
                <th className="text-right px-3 py-2 font-medium">{t("admin.queues.col.completed", "Klaar")}</th>
                <th className="text-right px-3 py-2 font-medium">{t("admin.queues.col.failed", "Mislukt")}</th>
                <th className="text-right px-3 py-2 font-medium">{t("admin.queues.col.delayed", "Uitgesteld")}</th>
                <th className="text-right px-5 py-2 font-medium">{t("admin.queues.col.concurrency", "Conc.")}</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.queues.map((qs) => (
                <tr key={qs.name} className="border-t border-neutral-100">
                  <td className="px-5 py-2.5 font-mono text-xs">{qs.name}</td>
                  <td className="px-5 py-2.5 font-mono text-xs text-neutral-500">{qs.cron ?? "–"}</td>
                  <td className="px-3 py-2.5 text-right text-neutral-700">{qs.counts.waiting}</td>
                  <td className="px-3 py-2.5 text-right text-brand-700">{qs.counts.active}</td>
                  <td className="px-3 py-2.5 text-right text-neutral-500">{qs.counts.completed}</td>
                  <td
                    className={`px-3 py-2.5 text-right ${qs.counts.failed > 0 ? "text-red-700 font-medium" : "text-neutral-500"}`}
                  >
                    {qs.counts.failed}
                  </td>
                  <td className="px-3 py-2.5 text-right text-neutral-500">{qs.counts.delayed}</td>
                  <td className="px-5 py-2.5 text-right text-neutral-500">{qs.concurrency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-neutral-500">
        {t(
          "admin.queues.help",
          "Live update elke 5s. Queues zonder processor (placeholder voor toekomstige features) blijven leeg."
        )}
      </p>
    </AdminLayout>
  );
}
